import { useState } from "react";
import { MapPin, ArrowUpRight, Waves, Landmark, Train, Car, Building2, Plane } from "lucide-react";
import { useT } from "@/i18n/LanguageProvider";

const ADDRESS = "Gaugasse 21, 65203 Wiesbaden";
const MAPS_QUERY = encodeURIComponent(ADDRESS);
const EMBED_SRC = `https://www.google.com/maps?q=${MAPS_QUERY}&output=embed&z=15`;
const EXTERNAL_SRC = `https://www.google.com/maps/search/?api=1&query=${MAPS_QUERY}`;

const LABEL_STYLE = { paintOrder: "stroke", stroke: "#FDFCFB", strokeWidth: 4, fontFamily: "Outfit, sans-serif" };

/**
 * LocationMap – interactive, stylised neighbourhood map of Wiesbaden-Biebrich
 * around Gaugasse 21. POI list and SVG markers highlight each other on hover;
 * a toggle switches to the real Google Maps embed.
 */
export const LocationMap = () => {
    const t = useT();
    const [active, setActive] = useState(null);
    const [view, setView] = useState("illustration");

    const POIS = [
        { id: "rheinufer", x: 330, y: 448, lx: -52, ly: 27, color: "#6E98A8", label: t({ de: "Rheinufer", en: "Rhine bank" }) },
        { id: "schloss", x: 592, y: 382, lx: -30, ly: -16, color: "#8A593A", label: t({ de: "Biebricher Schloss", en: "Biebrich Palace" }) },
        { id: "bahnhof", x: 216, y: 318, lx: 14, ly: 4, color: "#3D5A4B", label: t({ de: "Bf. Biebrich · S-Bahn", en: "Biebrich stn · S-Bahn" }) },
        { id: "a643", x: 128, y: 210, lx: 15, ly: 4, color: "#94AC8F", label: t({ de: "A643 · Schiersteiner Brücke", en: "A643 · Schierstein bridge" }) },
        { id: "city", x: 522, y: 92, lx: 14, ly: 4, color: "#6F8C72", label: t({ de: "Wiesbaden City · Hbf", en: "Wiesbaden centre · main stn" }) },
    ];

    const ITEMS = [
        { icon: Waves, poi: "rheinufer", label: t({ de: "Rheinufer & Promenade", en: "Rhine bank & promenade" }), dist: t({ de: "ca. 5 Min", en: "approx. 5 min" }) },
        { icon: Landmark, poi: "schloss", label: t({ de: "Biebricher Schloss & Schlosspark", en: "Biebrich Palace & park" }), dist: t({ de: "in der Nähe", en: "nearby" }) },
        { icon: Train, poi: "bahnhof", label: t({ de: "Bahnhof Wiesbaden-Biebrich (S-Bahn)", en: "Wiesbaden-Biebrich station (S-Bahn)" }), dist: t({ de: "wenige Minuten", en: "minutes away" }) },
        { icon: Car, poi: "a643", label: t({ de: "A643 · Richtung Mainz & Frankfurt", en: "A643 · towards Mainz & Frankfurt" }), dist: t({ de: "schnell erreichbar", en: "quick access" }) },
        { icon: Building2, poi: "city", label: t({ de: "Wiesbaden Hauptbahnhof & City", en: "Wiesbaden main station & centre" }), dist: t({ de: "ca. 12 Min", en: "approx. 12 min" }) },
        { icon: Plane, poi: null, label: t({ de: "Rhein-Main-Flughafen", en: "Frankfurt airport" }), dist: t({ de: "ca. 20 Min", en: "approx. 20 min" }) },
    ];

    return (
        <section id="lage" className="py-24 sm:py-32" data-testid="location-map-section">
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
                    <div className="lg:col-span-6">
                        <span className="font-body text-xs uppercase tracking-[0.22em] font-semibold text-terracotta-500">
                            {t({ de: "Die Lage", en: "The location" })}
                        </span>
                        <h2 className="font-display font-black tracking-tight text-sage-500 text-3xl sm:text-4xl lg:text-5xl mt-4 leading-tight text-balance">
                            {t({ de: "Biebrich vor der Tür,", en: "Biebrich at your door," })}
                            <span className="text-terracotta-400"> {t({ de: "der Rhein um die Ecke.", en: "the Rhine around the corner." })}</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-5 lg:col-start-8 self-end">
                        <p className="font-body text-lg text-sage-500/75 leading-relaxed">
                            {t({
                                de: "Die Nähe zum Rhein, das Biebricher Schloss und die sehr gute Infrastruktur machen den Standort besonders attraktiv. Mainz, Frankfurt und der Flughafen sind schnell erreichbar.",
                                en: "Proximity to the Rhine, Biebrich Palace and excellent infrastructure make this location especially attractive. Mainz, Frankfurt and the airport are easily reached.",
                            })}
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
                    {/* POI list */}
                    <div className="lg:col-span-5 order-2 lg:order-1 flex flex-col min-w-0">
                        <div className="space-y-2.5">
                            {ITEMS.map((item) => {
                                const Icon = item.icon;
                                const isActive = item.poi && active === item.poi;
                                return (
                                    <button
                                        key={item.label}
                                        type="button"
                                        onMouseEnter={() => item.poi && setActive(item.poi)}
                                        onMouseLeave={() => setActive(null)}
                                        onClick={() => item.poi && setActive(active === item.poi ? null : item.poi)}
                                        onFocus={() => item.poi && setActive(item.poi)}
                                        onBlur={() => setActive(null)}
                                        className={`w-full text-left flex items-center gap-4 px-4 py-3.5 rounded-2xl border transition-all duration-300 ${
                                            isActive
                                                ? "border-terracotta-300 bg-terracotta-50 -translate-x-0.5"
                                                : "border-black/5 bg-white hover:border-mint-300"
                                        }`}
                                        data-testid={`location-item-${item.poi || "flughafen"}`}
                                    >
                                        <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-colors ${isActive ? "bg-terracotta-300 text-white" : "bg-mint-100 text-sage-500"}`}>
                                            <Icon size={16} strokeWidth={1.8} />
                                        </span>
                                        <span className="flex-1 font-body text-sm text-sage-500 font-medium leading-snug">{item.label}</span>
                                        <span className="font-body text-xs text-sage-500/60 whitespace-nowrap">{item.dist}</span>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="mt-5 flex items-center gap-3 rounded-2xl bg-sage-500 text-white px-5 py-4" data-testid="location-address-card">
                            <MapPin size={20} className="text-terracotta-300 shrink-0" />
                            <div className="min-w-0">
                                <div className="font-body text-[10px] uppercase tracking-[0.2em] text-terracotta-300">
                                    {t({ de: "Adresse", en: "Address" })}
                                </div>
                                <div className="font-display font-bold text-base sm:text-lg leading-tight truncate">{ADDRESS}</div>
                            </div>
                            <a
                                href={EXTERNAL_SRC}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ml-auto inline-flex items-center gap-1.5 shrink-0 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 font-body text-sm transition-colors"
                                data-testid="open-maps"
                            >
                                {t({ de: "Route", en: "Route" })}
                                <ArrowUpRight size={14} />
                            </a>
                        </div>
                    </div>

                    {/* Map panel */}
                    <div className="lg:col-span-7 order-1 lg:order-2 min-w-0">
                        <div className="relative w-full rounded-[2rem] overflow-hidden border border-black/5 bg-mint-50 aspect-[4/3] lg:h-full lg:aspect-auto lg:min-h-[420px]" data-testid="location-map-panel">
                            <div className="absolute top-4 right-4 z-10 flex rounded-full bg-white/90 backdrop-blur border border-black/5 p-1 shadow-sm">
                                <button
                                    type="button"
                                    onClick={() => setView("illustration")}
                                    className={`px-4 py-1.5 rounded-full font-body text-xs font-medium transition-colors ${view === "illustration" ? "bg-sage-500 text-white" : "text-sage-500/70 hover:text-sage-500"}`}
                                    data-testid="location-view-toggle-illustration"
                                >
                                    {t({ de: "Umgebung", en: "Area" })}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setView("map")}
                                    className={`px-4 py-1.5 rounded-full font-body text-xs font-medium transition-colors ${view === "map" ? "bg-sage-500 text-white" : "text-sage-500/70 hover:text-sage-500"}`}
                                    data-testid="location-view-toggle-map"
                                >
                                    Google Maps
                                </button>
                            </div>

                            {view === "map" ? (
                                <iframe
                                    src={EMBED_SRC}
                                    title="Karte – Gaugasse 21, Wiesbaden"
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    className="absolute inset-0 w-full h-full border-0"
                                    data-testid="location-map-embed"
                                />
                            ) : (
                                <svg
                                    viewBox="0 0 800 620"
                                    className="absolute inset-0 w-full h-full"
                                    role="img"
                                    aria-label={t({ de: "Stilisierte Umgebungskarte von Wiesbaden-Biebrich", en: "Stylised map of Wiesbaden-Biebrich" })}
                                    data-testid="location-map-svg"
                                >
                                    <rect width="800" height="620" fill="#EAF0E7" />

                                    {/* Schlosspark */}
                                    <path d="M 520 330 C 560 305, 640 305, 675 330 C 706 352, 700 392, 665 405 C 620 421, 550 418, 525 392 C 505 371, 500 345, 520 330 Z" fill="#CBDCC2" />
                                    <circle cx="555" cy="345" r="7" fill="#A7BBA0" />
                                    <circle cx="600" cy="330" r="9" fill="#A7BBA0" />
                                    <circle cx="645" cy="352" r="7" fill="#A7BBA0" />
                                    <circle cx="620" cy="385" r="8" fill="#A7BBA0" />
                                    <text x="548" y="322" fontSize="11" fill="#6F8C72" style={LABEL_STYLE}>{t({ de: "Schlosspark", en: "Palace park" })}</text>

                                    {/* Rhine */}
                                    <path d="M 0 468 C 160 448, 320 462, 500 430 C 640 405, 730 398, 800 402 L 800 520 C 660 516, 560 530, 430 550 C 280 572, 130 566, 0 580 Z" fill="#C2D6DA" />
                                    <path d="M 60 505 C 180 492, 300 500, 430 480" stroke="#FFFFFF" strokeWidth="2" opacity="0.4" fill="none" strokeLinecap="round" />
                                    <path d="M 340 520 C 470 500, 600 478, 740 462" stroke="#FFFFFF" strokeWidth="2" opacity="0.4" fill="none" strokeLinecap="round" />
                                    <text x="618" y="474" fontSize="15" fontStyle="italic" fill="#4C7080" style={LABEL_STYLE}>Rhein</text>
                                    <text x="150" y="604" fontSize="12" fill="#3D5A4B" opacity="0.8" style={LABEL_STYLE}>{t({ de: "Mainz · ca. 15 Min", en: "Mainz · approx. 15 min" })}</text>

                                    {/* Streets */}
                                    <path d="M 250 210 C 330 225, 420 228, 500 214" stroke="#FFFFFF" strokeWidth="4" opacity="0.7" fill="none" strokeLinecap="round" />
                                    <path d="M 320 320 C 400 330, 480 322, 560 300" stroke="#FFFFFF" strokeWidth="4" opacity="0.7" fill="none" strokeLinecap="round" />
                                    <path d="M 30 452 C 200 428, 380 436, 540 405 C 650 384, 740 376, 800 374" stroke="#FFFFFF" strokeWidth="7" opacity="0.95" fill="none" strokeLinecap="round" />
                                    <path d="M 402 252 C 398 300, 394 360, 390 436" stroke="#FFFFFF" strokeWidth="7" opacity="0.95" fill="none" strokeLinecap="round" />
                                    <path d="M 402 252 C 340 262, 275 288, 218 320" stroke="#FFFFFF" strokeWidth="7" opacity="0.95" fill="none" strokeLinecap="round" />
                                    <path d="M 402 252 C 424 192, 468 130, 522 92" stroke="#FFFFFF" strokeWidth="7" opacity="0.95" fill="none" strokeLinecap="round" />

                                    {/* A643 highway with bridge */}
                                    <path d="M 128 60 C 118 180, 112 300, 122 392 C 130 462, 142 540, 152 612" stroke="#E4D3A8" strokeWidth="11" fill="none" strokeLinecap="round" />
                                    <path d="M 128 60 C 118 180, 112 300, 122 392 C 130 462, 142 540, 152 612" stroke="#FFFFFF" strokeWidth="2" strokeDasharray="8 7" fill="none" opacity="0.9" />

                                    {/* Small building texture near G21 */}
                                    <g fill="#C7D5C0" opacity="0.9">
                                        <rect x="356" y="216" width="18" height="13" rx="2" />
                                        <rect x="428" y="222" width="15" height="15" rx="2" />
                                        <rect x="372" y="278" width="16" height="12" rx="2" />
                                        <rect x="432" y="270" width="20" height="12" rx="2" />
                                    </g>

                                    {/* Compass */}
                                    <g transform="translate(745,50)">
                                        <circle r="18" fill="#FFFFFF" stroke="#C7D5C0" strokeWidth="1.5" />
                                        <text x="0" y="5" fontSize="13" fontWeight="700" fill="#3D5A4B" textAnchor="middle" fontFamily="Outfit, sans-serif">N</text>
                                        <path d="M 0 -14 L 4 -6 L -4 -6 Z" fill="#DFA882" />
                                    </g>

                                    {/* POIs */}
                                    {POIS.map((p) => {
                                        const isActive = active === p.id;
                                        return (
                                            <g
                                                key={p.id}
                                                transform={`translate(${p.x},${p.y})`}
                                                onMouseEnter={() => setActive(p.id)}
                                                onMouseLeave={() => setActive(null)}
                                                className="cursor-pointer"
                                                data-testid={`location-poi-${p.id}`}
                                            >
                                                {isActive && <circle r="17" fill={p.color} opacity="0.22" />}
                                                <circle r={isActive ? 9 : 6.5} fill={p.color} stroke="#FFFFFF" strokeWidth="2.5" style={{ transition: "r .2s ease" }} />
                                                <text x={p.lx} y={p.ly} fontSize={isActive ? 14 : 12.5} fontWeight="600" fill="#3D5A4B" style={LABEL_STYLE}>
                                                    {p.label}
                                                </text>
                                            </g>
                                        );
                                    })}

                                    {/* G21 marker */}
                                    <g transform="translate(402,250)" data-testid="location-poi-g21">
                                        <circle r="11" fill="#B0734A" opacity="0.5">
                                            <animate attributeName="r" from="11" to="30" dur="2.2s" repeatCount="indefinite" />
                                            <animate attributeName="opacity" from="0.45" to="0" dur="2.2s" repeatCount="indefinite" />
                                        </circle>
                                        <circle r="10" fill="#B0734A" stroke="#FFFFFF" strokeWidth="3" />
                                        <text x="0" y="4" fontSize="9" fontWeight="800" fill="#FFFFFF" textAnchor="middle" fontFamily="Outfit, sans-serif">21</text>
                                        <text x="17" y="-6" fontSize="14" fontWeight="700" fill="#8A593A" style={LABEL_STYLE}>G21 · Gaugasse 21</text>
                                    </g>
                                </svg>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
